<template>
  <div class="capture-record-list">
    <v-alert
      v-if="hasUnplacedItems"
      dense
      type="info"
      text
      class="mx-3 mb-0 text-caption"
    >
      {{ $t('dragHint') }}
    </v-alert>

    <div v-if="!hasAnyItems" class="capture-record-list__empty" data-testid="capture-record-empty">
      <v-icon large color="grey lighten-1">inventory_2</v-icon>
      <p>{{ $t('emptyMessage') }}</p>
    </div>

    <ul v-else class="capture-record-list__items">
      <li
        v-for="item in placeableItems"
        :key="item.id"
        :class="itemClasses(item)"
        :draggable="isDraggable(item)"
        data-testid="capture-item"
        @dragstart="handleDragStart($event, item)"
      >
        <v-icon
          small
          :color="getIconColor(item)"
          data-testid="capture-status"
        >{{ getIcon(item) }}</v-icon>
        <div class="capture-record-list__body">
          <div class="capture-record-list__head">
            <span class="capture-record-list__title">
              {{ speciesLabel(item.species) }} {{ item.count }}{{ $t('countUnit') }}
            </span>
          </div>
          <div v-if="item.isPlacedOnOtherFloorPlan" class="capture-record-list__placed-on">
            {{ $t('placedOnOther', { name: item.placedFloorPlanName }) }}
          </div>
          <div class="capture-record-list__tags">
            <span
              v-for="entry in item.sensorEntries"
              :key="entry.id"
              class="capture-record-list__tag"
            >{{ entry.name }}</span>
          </div>
          <div
            v-if="item.memo"
            class="capture-record-list__memo"
            data-testid="capture-memo"
          >{{ item.memo }}</div>
        </div>
        <div class="capture-record-list__more">
          <v-menu offset-y left content-class="capture-record-menu">
            <template #activator="{ on }">
              <v-btn icon x-small v-on="on" data-testid="capture-more">
                <v-icon small>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-if="item.isPlaced"
                data-testid="capture-menu-unplace"
                @click="$emit('unplace', item.record)"
              >
                <v-list-item-icon><v-icon small>mdi-pin-off</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('unplace') }}</v-list-item-title>
              </v-list-item>
              <v-list-item data-testid="capture-menu-edit" @click="$emit('edit', item.record)">
                <v-list-item-icon><v-icon small>edit</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item data-testid="capture-menu-delete" @click="openDeleteConfirm(item.record)">
                <v-list-item-icon><v-icon small color="error">delete</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">{{ $t('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </li>

      <template v-if="otherFloorPlanItems.length > 0">
        <li class="capture-record-list__divider">
          <list-section-divider
            :label="$t('otherFloorPlanSection')"
            testid="other-floor-plan-section-header"
          />
        </li>
        <li
          v-for="item in otherFloorPlanItems"
          :key="item.id"
          :class="itemClasses(item)"
          :draggable="false"
          data-testid="capture-item"
        >
          <v-icon
            small
            :color="getIconColor(item)"
            data-testid="capture-status"
          >{{ getIcon(item) }}</v-icon>
          <div class="capture-record-list__body">
            <div class="capture-record-list__head">
              <span class="capture-record-list__title">
                {{ speciesLabel(item.species) }} {{ item.count }}{{ $t('countUnit') }}
              </span>
            </div>
            <div v-if="item.isPlacedOnOtherFloorPlan" class="capture-record-list__placed-on">
              {{ $t('placedOnOther', { name: item.placedFloorPlanName }) }}
            </div>
            <div class="capture-record-list__tags">
              <span
                v-for="entry in item.sensorEntries"
                :key="entry.id"
                class="capture-record-list__tag"
              >{{ entry.name }}</span>
            </div>
            <div
              v-if="item.memo"
              class="capture-record-list__memo"
              data-testid="capture-memo"
            >{{ item.memo }}</div>
          </div>
          <div class="capture-record-list__more">
            <v-menu offset-y left content-class="capture-record-menu">
              <template #activator="{ on }">
                <v-btn icon x-small v-on="on" data-testid="capture-more">
                  <v-icon small>more_vert</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-if="item.isPlaced"
                  data-testid="capture-menu-unplace"
                  @click="$emit('unplace', item.record)"
                >
                  <v-list-item-icon><v-icon small>mdi-pin-off</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('unplace') }}</v-list-item-title>
                </v-list-item>
                <v-list-item data-testid="capture-menu-edit" @click="$emit('edit', item.record)">
                  <v-list-item-icon><v-icon small>edit</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item data-testid="capture-menu-delete" @click="openDeleteConfirm(item.record)">
                  <v-list-item-icon><v-icon small color="error">delete</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text">{{ $t('delete') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </li>
      </template>
    </ul>

    <confirm-dialog
      v-model="confirm.visible"
      :message="$t('confirm.delete_capture_record')"
      @yes="handleConfirmYes"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  dragHint: 記録を図面にドラッグして配置できます
  emptyMessage: この月の捕獲記録はありません
  otherFloorPlanSection: 他の図面に配置済み
  countUnit: 頭
  edit: 編集
  delete: 削除
  unplace: 配置を解除
  placedOnOther: "[{name}] に配置済み"
  confirm:
    delete_capture_record: 捕獲記録を削除します。よろしいですか？
  species:
    brown_rat: クマネズミ
    black_rat: ドブネズミ
    house_mouse: ハツカネズミ
    other: その他
    unknown: 不明
en:
  dragHint: Drag records onto the floor plan to place them
  emptyMessage: No capture records for this month
  otherFloorPlanSection: Placed on another floor plan
  countUnit: ""
  edit: Edit
  delete: Delete
  unplace: Remove from floor plan
  placedOnOther: "Placed on [{name}]"
  confirm:
    delete_capture_record: Are you sure you want to delete this capture record?
  species:
    brown_rat: Brown Rat
    black_rat: Black Rat
    house_mouse: House Mouse
    other: Other
    unknown: Unknown
</i18n>

<script>
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import ListSectionDivider from '@/components/atoms/ListSectionDivider';

export default {
  name: 'CaptureRecordList',

  components: {
    ConfirmDialog,
    ListSectionDivider,
  },

  props: {
    captureRecords: { type: Array, default: () => [] },
    floorPlanObjects: { type: Object, default: () => ({}) },
    currentFloorPlanId: { type: String, default: null },
    allFloorPlans: { type: Array, default: () => [] },
    sensors: { type: Array, default: () => [] },
  },

  data() {
    return {
      confirm: {
        visible: false,
        processing: false,
        onConfirm: () => {},
      },
    };
  },

  computed: {
    sensorNameMap() {
      const map = {};
      this.sensors.forEach(s => { map[s.id] = s.name; });
      return map;
    },

    items() {
      return this.captureRecords.map(record => {
        const placement = this.floorPlanObjects[record.id];
        const placedFloorPlan = this.findPlacedFloorPlan(record.id);
        const isPlaced = placement != null;
        const isPlacedOnOtherFloorPlan = !isPlaced && placedFloorPlan != null;

        return {
          id: record.id,
          record,
          species: record.species,
          count: record.count,
          memo: record.memo,
          sensorEntries: (record.sensor_ids || []).map(id => ({
            id,
            name: this.sensorNameMap[id] || id,
          })),
          isPlaced,
          isPlacedOnOtherFloorPlan,
          placedFloorPlanName: isPlacedOnOtherFloorPlan ? placedFloorPlan.name : null,
        };
      });
    },

    hasUnplacedItems() {
      return this.items.some(item => !item.isPlaced && !item.isPlacedOnOtherFloorPlan);
    },

    placeableItems() {
      const placedHere = this.items.filter(i => i.isPlaced);
      const unplaced = this.items.filter(i => !i.isPlaced && !i.isPlacedOnOtherFloorPlan);
      return [...placedHere, ...unplaced];
    },

    otherFloorPlanItems() {
      return this.items.filter(i => i.isPlacedOnOtherFloorPlan);
    },

    hasAnyItems() {
      return this.items.length > 0;
    },
  },

  methods: {
    findPlacedFloorPlan(recordId) {
      return this.allFloorPlans.find(
        fp => fp.id !== this.currentFloorPlanId && fp.objects && fp.objects[recordId] != null,
      ) || null;
    },

    speciesLabel(species) {
      return this.$t(`species.${species}`);
    },

    isDraggable(item) {
      return !item.isPlaced && !item.isPlacedOnOtherFloorPlan;
    },

    itemClasses(item) {
      return {
        'capture-record-list__item': true,
        'capture-record-list__item--placed': item.isPlaced,
        'capture-record-list__item--disabled': item.isPlacedOnOtherFloorPlan,
      };
    },

    getIconColor(item) {
      if (item.isPlaced) return 'success';
      if (item.isPlacedOnOtherFloorPlan) return 'warning';
      return 'grey';
    },

    getIcon(item) {
      // 配置済み（現図面・別図面とも）はチェックアイコン。色はgetIconColorで区別
      if (item.isPlaced || item.isPlacedOnOtherFloorPlan) return 'mdi-check-circle';
      return 'mdi-checkbox-blank-circle-outline';
    },

    handleDragStart(event, item) {
      if (!this.isDraggable(item)) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData('text/plain', JSON.stringify({
        type: 'capture',
        id: item.id,
      }));
      event.dataTransfer.effectAllowed = 'move';
    },

    openDeleteConfirm(record) {
      this.confirm.onConfirm = () => this.$emit('delete', record);
      this.confirm.processing = false;
      this.confirm.visible = true;
    },

    handleConfirmYes() {
      if (this.confirm.processing) return;
      this.confirm.processing = true;
      this.confirm.onConfirm();
    },

  },
};
</script>

<style scoped>
.capture-record-list__items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.capture-record-list__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: grab;
}

.capture-record-list__item--placed {
  cursor: default;
}

/* 他の図面に配置済み: 区切りセクションで状態が読めるため透過は使わない */
.capture-record-list__item--disabled {
  cursor: default;
}

.capture-record-list__divider {
  list-style: none;
}

.capture-record-list__body {
  flex: 1;
  min-width: 0;
}

.capture-record-list__title {
  display: inline-block;
  max-width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: #757575;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.capture-record-list__placed-on {
  font-size: 11px;
  color: #ff9800;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.capture-record-list__tags {
  margin-top: 4px;
}

.capture-record-list__tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e3f0fb;
  color: #4a72b8;
  font-size: 11px;
  margin: 0 3px 2px 0;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.capture-record-list__memo {
  margin-top: 4px;
  font-size: 11px;
  color: #9e9e9e;
  line-height: 1.4;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.capture-record-list__empty {
  padding: 32px 16px;
  text-align: center;
  color: #9e9e9e;
}

.capture-record-list__empty p {
  margin: 8px 0 0;
  font-size: 14px;
}
</style>

<style>
/* v-menuのコンテンツはbody直下にテレポートされるためscopedが効かない。
   Vuetifyの `.v-application--is-ltr .v-list-item__icon:first-child` (0,3,0) に勝つため !important。 */
.capture-record-menu .v-list-item__icon,
.capture-record-menu .v-list-item__icon:first-child {
  min-width: unset !important;
  margin: 0 4px 0 0 !important;
  align-self: center;
}
</style>
